import { useId, useRef, useState } from "react";
import { ApiError, userFacingMessage } from "../api/errors";
import { useAuth } from "../auth/AuthContext";
import { useCollection } from "../collection/CollectionContext";
import { Button } from "./Button";
import { ErrorMessage } from "./ErrorMessage";
import {
  buildCollectionExport,
  collectionExportFilename,
  downloadCollectionExport,
} from "../portability/export-collection";
import {
  readImportFile,
  validateCollectionImportJson,
  type ImportValidationSuccess,
} from "../portability/import-validation";

/**
 * Collection export/import UI owner. Used by Profile; not duplicated on Collection.
 */
export function CollectionPortabilityPanel() {
  const auth = useAuth();
  const collection = useCollection();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importPreview, setImportPreview] =
    useState<ImportValidationSuccess | null>(null);
  const [importBusy, setImportBusy] = useState(false);

  const isDemo = auth.status === "authenticated" && auth.account.isDemo;
  const ready = collection.status === "ready";
  const exportDisabled = !collection.online || !ready;
  const importDisabled = !collection.online || !ready || isDemo;

  async function handleExport() {
    setExportError(null);
    setExportBusy(true);
    try {
      const snapshot = await collection.exportFresh();
      const document = buildCollectionExport(snapshot);
      downloadCollectionExport(document, collectionExportFilename());
    } catch (error) {
      setExportError(
        error instanceof Error ? error.message : userFacingMessage(error),
      );
    } finally {
      setExportBusy(false);
    }
  }

  async function handleFileSelected(file: File | undefined) {
    setImportError(null);
    setImportPreview(null);
    if (!file) {
      return;
    }
    try {
      const text = await readImportFile(file);
      const result = validateCollectionImportJson(text);
      if (!result.ok) {
        setImportError(result.message);
        return;
      }
      setImportPreview(result);
    } catch (error) {
      setImportError(
        error instanceof Error ? error.message : "Не удалось прочитать файл.",
      );
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function handleConfirmImport() {
    if (!importPreview) {
      return;
    }
    setImportBusy(true);
    setImportError(null);
    try {
      await collection.importReplace(importPreview.items);
      setImportPreview(null);
    } catch (error) {
      if (error instanceof ApiError && error.code === "IMPORT_UNKNOWN_RELEASE") {
        setImportError(
          "Импорт отклонён: в каталоге нет одного или нескольких релизов.",
        );
      } else if (!(error instanceof ApiError && error.code === "COLLECTION_CONFLICT")) {
        setImportError(userFacingMessage(error));
      }
    } finally {
      setImportBusy(false);
    }
  }

  return (
    <section className="hw-profile-section" aria-labelledby="profile-data-heading">
      <h2 id="profile-data-heading" className="hw-profile-section__title">
        Данные коллекции
      </h2>

      {!isDemo ? (
        <input
          ref={fileInputRef}
          id={fileInputId}
          type="file"
          accept="application/json,.json"
          className="hw-visually-hidden"
          aria-label="Выбрать JSON-файл коллекции"
          onChange={(event) => {
            void handleFileSelected(event.target.files?.[0]);
          }}
        />
      ) : null}

      <div className="hw-profile-actions">
        <Button
          type="button"
          variant="ghost"
          disabled={exportDisabled || exportBusy}
          onClick={() => {
            void handleExport();
          }}
        >
          Экспорт
        </Button>
        {!isDemo ? (
          <Button
            type="button"
            variant="ghost"
            disabled={importDisabled || importBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            Импорт
          </Button>
        ) : (
          <p className="hw-profile-note" role="note">
            Импорт недоступен в демо-режиме.
          </p>
        )}
      </div>

      {exportError ? <ErrorMessage>{exportError}</ErrorMessage> : null}
      {importError ? <ErrorMessage>{importError}</ErrorMessage> : null}
      {collection.conflictMessage ? (
        <ErrorMessage>{collection.conflictMessage}</ErrorMessage>
      ) : null}

      {importPreview ? (
        <div className="hw-import-preview" role="dialog" aria-label="Подтверждение импорта">
          <p>
            Импорт заменит коллекцию: {importPreview.itemCount} позиций, суммарно{" "}
            {importPreview.totalQuantity} шт.
          </p>
          <div className="hw-import-preview__actions">
            <Button
              type="button"
              disabled={importBusy}
              onClick={() => {
                void handleConfirmImport();
              }}
            >
              Применить импорт
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={importBusy}
              onClick={() => setImportPreview(null)}
            >
              Отмена
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
